import { UIPrevision } from "@/types/prevision-type";
import { formatDatePrevisionTable } from "@/helpers/format-date";
import { formatWeatherStateIcon } from "@/helpers/format-weather-state-icon";
import { useTranslation } from "react-i18next";
import { IHeaderTableForecast } from "@/types/header-forecast-table";
import { useWindowSize } from "@/hooks/use-window-resize";

import WindDirection from "@/components/wind-direction/WindDirection";
import AirPollution from "../air-pollution/AirPollution";

import "./style.scss";

type PrevisionTableProps = {
	labelHeader: IHeaderTableForecast[];
	data: UIPrevision[];
	day: string;
	date: string;
	type: "marine" | "terrestrial";
};

export const PrevisionTable = ({
	data,
	day,
	date,
	labelHeader,
	type,
}: PrevisionTableProps) => {
	const width = useWindowSize();
	const colSpan = labelHeader.length;
	const now = new Date();
	const { t } = useTranslation();

	const currentHourDate = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate(),
		now.getHours(),
		0,
		0,
		0,
	);

	currentHourDate.setHours(currentHourDate.getHours() - 1);

	return (
		<table className="prevision-table">
			<thead>
				<tr>
					{labelHeader.map((item) => {
						const { icon, key } = item;
						const textTranslate = t(key);
						return (
							<th key={key}>
								{width > 950 ? (
									textTranslate
								) : icon ? (
									<img src={icon} alt={textTranslate} />
								) : (
									textTranslate
								)}
							</th>
						);
					})}
				</tr>
			</thead>
			<tbody>
				<tr className="date-tr">
					<td colSpan={colSpan}>
						<p>
							{day} {date}
						</p>
					</td>
				</tr>
				{data.length > 0 ? (
					data.map((item, idx) => {
						if (type === "marine") {
							return (
								<tr
									key={idx}
									className={
										item.timestep &&
										`${
											new Date(item.timestep) >= currentHourDate ? null : "past"
										}`
									}
								>
									<td>{formatDatePrevisionTable(item.timestep ?? "")}</td>
									<td>{item.temperature_2m ?? "-"}</td>
									<td>{item.wind_speed_10m ?? "-"}</td>
									<td className="wind-direction-td">
										{item?.wind_direction_10m != undefined ? (
											<WindDirection
												color="var(--wp--preset--color--text-primary)"
												rotation={item.wind_direction_10m}
											/>
										) : (
											"-"
										)}
									</td>
									<td>{item.wave_height?.toFixed(1) ?? "-"}</td>
									<td className="wind-direction-td wave-direction">
										{item.wind_direction_10m ? (
											<WindDirection
												color={"#3BA3E2"}
												rotation={item.wind_direction_10m}
											/>
										) : (
											"-"
										)}
									</td>
									<td>{item.current_speed ?? "-"}</td>
									<td>{item.wave_period?.toFixed(1) ?? "-"}</td>
									<td>{item.sea_surface_temperature ?? "-"}</td>
									<td>{item.tidal_elevation?.toFixed(1) ?? "-"}</td>
								</tr>
							);
						} else {
							const weatherIcon = item.timestep
								? formatWeatherStateIcon({
										status: item.ww,
										timeStep: item.timestep,
								  })
								: undefined;
							return (
								<tr
									key={idx}
									className={
										item.timestep &&
										`${
											new Date(item.timestep) >= currentHourDate ? null : "past"
										}`
									}
								>
									<td>
										{item.timestep
											? formatDatePrevisionTable(item.timestep)
											: "-"}
									</td>
									<td>
										{weatherIcon ? (
											<img src={weatherIcon} alt="weather-icon" />
										) : (
											"-"
										)}
									</td>
									<td>{item.temperature_2m ?? "-"}</td>
									<td>{item.precipitation_accumulation_3h ?? "-"}</td>
									<td className="wind-direction-td">
										{item?.wind_direction_10m != undefined ? (
											<WindDirection
												color={"#143769"}
												rotation={item.wind_direction_10m}
											/>
										) : (
											"-"
										)}
									</td>
									<td>{item.wind_speed_10m ?? "-"}</td>
									<td>{item.relative_humidity_10m ?? "-"}</td>
									<td>
										<AirPollution index={item.air_pollution_index} />
									</td>
								</tr>
							);
						}
					})
				) : (
					<tr className="date-tr">
						<td colSpan={colSpan}>
							<p>
								No data to display for {day} {date}
							</p>
						</td>
					</tr>
				)}
			</tbody>
		</table>
	);
};

export default PrevisionTable;
